/*
 * The master inbox's list column (`page.tsx` composes it), in the reply agent's list frame
 * (master-inbox plan, step 3): the heading line with the view's name and its view menu, the label
 * tabs that stand in for the rail's rows while it is collapsed, the incumbent bulk bar that takes the
 * heading's place while rows are ticked, and the list body with its optional Highlights strip, its
 * empty and failed states and its pages. One flat list, newest first by default: no Do first shelf
 * and no fold (Dawid, 2026-10-17, "Flat + Needs reply view").
 */
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { appCount } from "../../src/app-dates.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import {
  INBOX_PAGE_SIZE,
  type InboxConversation,
  type InboxCounts,
  type InboxPage,
} from "../../src/inbox-v2/contract.ts";
import { BulkBar } from "../bulk-bar.tsx";
import { CheckIcon, ChevronDownIcon, ClockIcon, MoreIcon, ScopeIcon } from "../icons.tsx";
import { type MenuRow, OptionMenu } from "../option-menu.tsx";
import { Pager } from "../pager.tsx";
import { PendingCount } from "../pending-count.tsx";
import { type MailChoice, type NavRow, type ShellNav, sameChoice } from "../shell-nav.ts";
import { AssignMenu, type Assignment } from "./assignment.tsx";
import { LABEL_VIEWS } from "./labels.ts";
import { ConversationRow } from "./list.tsx";
import { type EmptyContext, ListEmpty, ListFailed } from "./list-empty.tsx";
import type { ListMode, ListSort } from "./list-prefs.ts";
import { RelabelMenu } from "./thread.tsx";
import type { NextPlace, Toast } from "./use-inbox.ts";

const conversations = (n: number) => `${appCount(n)} ${n === 1 ? "conversation" : "conversations"}`;

/**
 * A count beside a name, read with its noun: "Inbox, 60 conversations", not "Inbox60". "pending"
 * holds the number's place with a bar while it is on its way (design review "Loading", P3).
 */
function Count(props: { value: number | "pending" | undefined }) {
  if (props.value === undefined) return null;
  if (props.value === "pending") return <PendingCount className="lcr-num" />;
  return (
    <span className="lcr-num">
      <span aria-hidden="true">{appCount(props.value)}</span>
      <span className="lcr-visually-hidden">, {conversations(props.value)}</span>
    </span>
  );
}

/*
 * The tabs that stand in for the rail's mail rows while the rail is icons only, and on a phone: Inbox,
 * home, then the work, Meeting requests and Needs reply with their totals (LEA-494: the two split
 * what needs a reply, so both are in view), then Follow-up due and the label views behind one More
 * menu, as the reply agent's own tabs keep their rarer groups ("Your move, Waiting on lead, More").
 * Like their rail rows, Inbox and the labels carry no total; Follow-up due says its total in the menu
 * (LEA-485, LEA-486).
 *
 * Measured at 390 px (LEA-494): the three named tabs and a worded More need about 393 px, more than
 * a phone's line, so More is its ⋯ mark until one of its views is chosen, and then that view's name.
 * The tabs and More are one strip, so the order menu that does not fit beside them takes the line
 * above (the heading's own rule) and More stays with the tabs, wrapping under them only when a long
 * view name does not fit.
 */
/**
 * The inbox's panel, folded (LEA-501): where the window is too narrow for it, its rows come here.
 * The tabs' More menu gains the boxes, the saved views and the folders, each opened by its address,
 * and the Showing choice stands beside the tabs; the rail's Inbox icon carries the stroke.
 */
export type FoldedPanel = {
  readonly nav: ShellNav;
  readonly onNavigate: (href: string) => void;
};

/** The panel's rows a folded More menu opens by address: the boxes, then the views and folders. */
const FOLDED_BOXES = new Set(["snoozed", "sent", "done", "trash"]);
const HREF = "href:";

function foldedRows(nav: ShellNav): {
  boxes: readonly NavRow[];
  all: readonly NavRow[];
} {
  const boxes = nav.mail.filter((row) => FOLDED_BOXES.has(row.key));
  return { boxes, all: [...boxes, ...nav.views, ...nav.more] };
}

const hrefOption = (row: NavRow) => ({
  value: `${HREF}${row.href}`,
  label: row.label,
  ...(row.count === null ? {} : { count: { value: row.count, noun: "conversations" } }),
});

/** The rows a folded panel adds to the More menu, after Follow-up due and the labels. */
function foldedOptions(nav: ShellNav): MenuRow<string>[] {
  const { boxes } = foldedRows(nav);
  return [
    ...boxes.map(hrefOption),
    ...(nav.views.length > 0
      ? [{ key: "views", label: "Views", submenu: nav.views.map(hrefOption) }]
      : []),
    ...(nav.more.length > 0
      ? [{ key: "folders", label: "More folders", submenu: nav.more.map(hrefOption) }]
      : []),
  ];
}

/** The shell's panel folded: on a desktop too narrow for it, on a page of a workspace's inbox. */
export function foldedPanelOf(shell: {
  readonly onPhone: boolean;
  readonly fitted: { readonly panel: number };
  readonly nav: ShellNav | null;
  readonly leave: (href: string) => void;
}): FoldedPanel | null {
  if (shell.onPhone || shell.fitted.panel > 0 || !shell.nav || shell.nav.mail.length === 0)
    return null;
  return { nav: shell.nav, onNavigate: shell.leave };
}

/** Whose threads the list holds, beside the tabs while the panel that holds it is folded. */
function FoldedShowing({ folded }: { folded: FoldedPanel }) {
  const scope = folded.nav.scope;
  if (!scope) return null;
  const choices = [...scope.fixed, ...scope.people];
  return (
    <OptionMenu<string>
      label="Showing"
      icon={<ScopeIcon size={16} />}
      className="lcr-list-showing"
      value={scope.value}
      display={scope.label}
      options={choices.map(({ value, label }) => ({ value, label }))}
      onChange={(value) => {
        const href = choices.find((choice) => choice.value === value)?.href;
        if (href) folded.onNavigate(href);
      }}
    />
  );
}

function LabelTabs(props: {
  choice: MailChoice;
  counts: InboxCounts | undefined;
  onChoose: (choice: MailChoice) => void;
  /** The folded panel's rows and Showing; absent on a phone, whose menu sheet holds them. */
  folded?: FoldedPanel | undefined;
}) {
  const { counts, choice } = props;
  const tab = (target: MailChoice, name: string, count: number | undefined) => (
    <button
      type="button"
      className="lcr-tab"
      aria-pressed={sameChoice(choice, target)}
      onClick={() => props.onChoose(target)}
    >
      {name}
      <Count value={count} />
    </button>
  );
  const label = choice.kind === "label" ? choice.label : null;
  const current = LABEL_VIEWS.find((view) => view.view === label);
  const followUp = choice.kind === "follow_up";
  const followUps = counts?.follow_up;
  const placed = props.folded ? foldedRows(props.folded.nav).all.find((row) => row.current) : null;
  const chosen = followUp ? "Follow-up due" : (current?.name ?? placed?.label ?? null);
  return (
    <div className="lcr-list-tab-strip">
      {tab({ kind: "box", box: "inbox" }, "Inbox", undefined)}
      {tab({ kind: "meeting" }, "Meeting requests", counts?.meeting)}
      {tab({ kind: "needs_reply" }, "Needs reply", counts?.needs_reply)}
      <OptionMenu<string>
        label="More views"
        icon={null}
        className="lcr-more-groups"
        pressed={chosen !== null}
        value={followUp ? "follow_up" : (current?.view ?? (placed ? `${HREF}${placed.href}` : ""))}
        display={chosen ?? <MoreIcon size={16} />}
        caret={chosen !== null}
        options={[
          {
            value: "follow_up",
            label: "Follow-up due",
            ...(followUps === undefined ? {} : { detail: conversations(followUps) }),
          },
          ...LABEL_VIEWS.map(({ view, name }) => ({ value: view, label: name })),
          ...(props.folded ? foldedOptions(props.folded.nav) : []),
        ]}
        onChange={(value) => {
          if (value.startsWith(HREF)) {
            props.folded?.onNavigate(value.slice(HREF.length));
            return;
          }
          const view = LABEL_VIEWS.find((item) => item.view === value)?.view;
          if (value === "follow_up") props.onChoose({ kind: "follow_up" });
          else if (view) props.onChoose({ kind: "label", label: view });
        }}
      />
      {props.folded && <FoldedShowing folded={props.folded} />}
    </div>
  );
}

const TAB_ROWS = new Set(["inbox", "meeting", "needs_reply"]);

/**
 * The folded panel on a page with no tabs of its own, a More folder or All mail (LEA-501): the inbox's
 * strip, each row opened by its address, More naming the page. Without it, between the panel's fold
 * and a phone, such a page had no way to the other rows but the rail's Inbox. No Showing: a folder's
 * list is nobody's in particular, so the scope would change nothing on screen.
 */
export function FoldedPlaces({ folded }: { folded: FoldedPanel }) {
  const { nav } = folded;
  const tabs = nav.mail.filter((row) => TAB_ROWS.has(row.key));
  const work = nav.mail.filter((row) => !TAB_ROWS.has(row.key) && !FOLDED_BOXES.has(row.key));
  const placed = [...work, ...foldedRows(nav).all].find((row) => row.current) ?? null;
  return (
    <div className="lcr-list-tabs lcr-list-heading lcr-folded-places">
      <div className="lcr-list-tab-strip">
        {tabs.map((row) => (
          <button
            key={row.key}
            type="button"
            className="lcr-tab"
            aria-pressed={row.current}
            onClick={() => folded.onNavigate(row.href)}
          >
            {row.label}
            <Count value={row.count ?? undefined} />
          </button>
        ))}
        <OptionMenu<string>
          label="More views"
          icon={null}
          className="lcr-more-groups"
          pressed={placed !== null}
          value={placed ? `${HREF}${placed.href}` : ""}
          display={placed?.label ?? <MoreIcon size={16} />}
          caret={placed !== null}
          options={[...work.map(hrefOption), ...foldedOptions(nav)]}
          onChange={(value) => folded.onNavigate(value.slice(HREF.length))}
        />
      </div>
    </div>
  );
}

const SORTS: readonly { value: ListSort; label: string; short: string }[] = [
  { value: "newest", label: "Newest first", short: "Newest" },
  { value: "oldest", label: "Oldest first", short: "Oldest" },
];
const MODES: readonly { value: ListMode; label: string; detail: string }[] = [
  { value: "classic", label: "Classic", detail: "Every reply in one list" },
  {
    value: "highlights",
    label: "Highlights",
    detail: "Up to 3 needing a reply, pinned on top",
  },
];

/** Where an arrow, Home or End moves among a menu's items; undefined for any other key. */
function menuTarget(items: HTMLElement[], key: string): HTMLElement | undefined {
  const active = document.activeElement;
  const index = active instanceof HTMLElement ? items.indexOf(active) : -1;
  if (key === "ArrowDown") return items[(index + 1) % items.length];
  if (key === "ArrowUp") return items[(index - 1 + items.length) % items.length];
  if (key === "Home") return items[0];
  return key === "End" ? items.at(-1) : undefined;
}

const menuItems = (root: HTMLElement | null): HTMLElement[] => [
  ...(root?.querySelectorAll<HTMLElement>("[role=menuitem], [role=menuitemradio]") ?? []),
];

function MenuChoice(props: {
  checked: boolean;
  label: string;
  detail?: string;
  /** Why the choice cannot be made now, said in its place; it stays in the arrows' path. */
  unavailable?: string;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={props.checked}
      aria-disabled={props.unavailable ? true : undefined}
      className="lcr-menu-item"
      onClick={props.unavailable ? undefined : props.onChoose}
    >
      <span className="lcr-menu-item-text">
        <span className="lcr-menu-item-label">{props.label}</span>
        {(props.unavailable ?? props.detail) && (
          <span className="lcr-menu-item-detail">{props.unavailable ?? props.detail}</span>
        )}
      </span>
      {props.checked ? (
        <CheckIcon size={16} />
      ) : (
        <span className="lcr-menu-item-tick-room" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * The heading's view menu: the order, kept per view, and the list's mode, kept per person. It says
 * the order on its face ("Newest"), as the list's heading should; the mode is one choice inside it,
 * not a toggle on the page. Built from the option menu's own parts, with two groups of choices.
 *
 * On a phone it also starts the selection, beside a long press on a row, as the reply agent's
 * filter tray does: the heading carries no Select of its own (master-inbox plan, "Phone behaviour").
 */
export function ViewMenu(props: {
  sort: ListSort;
  /** A reply agent status lists newest first only (step 9): Oldest says so and cannot be chosen. */
  newestOnly?: boolean;
  mode: ListMode;
  onSort: (sort: ListSort) => void;
  onMode: (mode: ListMode) => void;
  /** A phone's way into the selection; absent on a desktop, which ticks rows by hover or X. */
  onSelect?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const items = () => menuItems(root.current);
  useEffect(() => {
    if (!open) return;
    menuItems(root.current)[0]?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };
  const sort = SORTS.find((choice) => choice.value === props.sort) ?? SORTS[0];
  const mode = MODES.find((choice) => choice.value === props.mode) ?? MODES[0];
  const choose = (apply: () => void) => () => {
    apply();
    close();
  };
  return (
    <div className="lcr-option-menu lcr-more-groups lcr-view-menu" ref={root}>
      <button
        ref={button}
        type="button"
        className="lcr-option-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`View: ${sort?.label}, ${mode?.label}`}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" || open) return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span className="lcr-option-value">{sort?.short}</span>
        <ChevronDownIcon size={14} className="lcr-option-caret" />
      </button>
      {open && (
        <div
          className="lcr-popover lcr-option-popover"
          id={menuId}
          role="menu"
          aria-label="View"
          onKeyDown={(event) => {
            if (event.key === "Escape" || event.key === "Tab") {
              if (event.key === "Escape") event.preventDefault();
              return close();
            }
            const target = menuTarget(items(), event.key);
            if (!target) return;
            event.preventDefault();
            target.focus();
          }}
        >
          {props.onSelect && (
            <>
              <button
                type="button"
                role="menuitem"
                className="lcr-menu-item"
                onClick={() => {
                  close();
                  props.onSelect?.();
                }}
              >
                <span className="lcr-menu-item-text">
                  <span className="lcr-menu-item-label">Select conversations</span>
                </span>
              </button>
              <hr className="lcr-view-menu-rule" />
            </>
          )}
          {/* biome-ignore lint/a11y/useSemanticElements: a group of radio items inside role=menu, where a fieldset is not allowed. */}
          <div role="group" aria-label="Sort">
            <p className="lcr-label">Sort</p>
            {SORTS.map((choice) => (
              <MenuChoice
                key={choice.value}
                checked={choice.value === props.sort}
                label={choice.label}
                {...(props.newestOnly && choice.value === "oldest"
                  ? { unavailable: "Reply agent states list newest first" }
                  : {})}
                // Newest-only keeps the view's own stored order for when the agent state is off.
                onChoose={choose(() => (props.newestOnly ? undefined : props.onSort(choice.value)))}
              />
            ))}
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: a group of radio items inside role=menu, where a fieldset is not allowed. */}
          <div role="group" aria-label="List">
            <p className="lcr-label">List</p>
            {MODES.map((choice) => (
              <MenuChoice
                key={choice.value}
                checked={choice.value === props.mode}
                label={choice.label}
                detail={choice.detail}
                onChoose={choose(() => props.onMode(choice.value))}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The heading line: the view's name and total, or, while the rail is icons only, the tabs that name
 * it; then the view menu. It is the line the bulk bar replaces on desktop.
 */
export function ListHeading(props: {
  name: string;
  /** The list's total; "pending" while the list is on its way, undefined where none is known. */
  total: number | "pending" | undefined;
  tabs: boolean;
  /** The inbox's panel, folded on a desktop: its rows join the tabs (LEA-501). */
  folded?: FoldedPanel | undefined;
  choice: MailChoice;
  counts: InboxCounts | undefined;
  menu: ReactNode;
  onChoose: (choice: MailChoice) => void;
}) {
  return (
    <div className="lcr-list-tabs lcr-list-heading">
      {props.tabs ? (
        <LabelTabs
          choice={props.choice}
          counts={props.counts}
          onChoose={props.onChoose}
          folded={props.folded}
        />
      ) : (
        <h2 className="lcr-list-title">
          {props.name}
          <Count value={props.total} />
        </h2>
      )}
      <span className="lcr-list-heading-end">{props.menu}</span>
    </div>
  );
}

/**
 * How many rows are ticked, said politely while the rep selects (the bar draws the count for the
 * eye only). Always in the page, so the first tick is announced, and empty when nothing is ticked.
 */
export function SelectionStatus(props: { selecting: boolean; count: number }) {
  return (
    <p className="lcr-visually-hidden" role="status">
      {props.selecting && props.count > 0 ? `${appCount(props.count)} selected` : ""}
    </p>
  );
}

/**
 * The incumbent bulk bar (`bulk-bar.tsx`) with the master inbox's own two actions ahead of its
 * reading pair: Done, Delete (Restore in Trash) and Relabel. Its keys are drawn as the bar draws its own and hidden where the
 * page shows none.
 */
export function SelectionBar(props: {
  count: number;
  header: "none" | "some" | "all";
  relabelOpen: boolean;
  /** The Snoozed box: the bar wakes them instead (H still opens the times). */
  snoozed: boolean;
  /** Snooze's choices, while open from this bar. */
  snoozeMenu: ReactNode;
  onToggleAll: () => void;
  onDone: () => void;
  /** Trash's own list: the bar restores instead of deleting (LEA-457). */
  inTrash: boolean;
  onTrash: () => void;
  onSeen: (seen: boolean) => void;
  onRelabelOpen: (open: boolean) => void;
  onRelabel: (label: InboxLabelCode | null) => void;
  onSnooze: () => void;
  onUnsnooze: () => void;
  onClear: () => void;
  /** Assign for the ticked rows, where assignment is on; null draws no button. */
  assign: {
    readonly assignment: Assignment;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onChoose: (value: string) => void;
  } | null;
}) {
  const empty = props.count === 0;
  const { assign } = props;
  return (
    <BulkBar
      header={props.header}
      count={{ long: `${appCount(props.count)} selected`, short: appCount(props.count) }}
      empty={empty}
      onToggleAll={props.onToggleAll}
      onRead={() => props.onSeen(true)}
      onUnread={() => props.onSeen(false)}
      onClear={props.onClear}
      extra={
        <>
          <button
            type="button"
            className="lcr-bulk-action"
            aria-keyshortcuts="E"
            title="Done (E)"
            disabled={empty}
            onClick={props.onDone}
          >
            Done
            <kbd className="lcr-kbd">E</kbd>
          </button>
          <button
            type="button"
            className="lcr-bulk-action"
            aria-keyshortcuts="#"
            title={props.inTrash ? "Restore (#)" : "Delete (#)"}
            disabled={empty}
            onClick={props.onTrash}
          >
            {props.inTrash ? "Restore" : "Delete"}
            <kbd className="lcr-kbd">#</kbd>
          </button>
          <span className="lcr-i2-menu-anchor">
            <button
              type="button"
              className="lcr-bulk-action"
              aria-haspopup="menu"
              aria-expanded={props.relabelOpen}
              aria-keyshortcuts="L"
              disabled={empty}
              onClick={() => props.onRelabelOpen(!props.relabelOpen)}
            >
              Relabel
              <kbd className="lcr-kbd">L</kbd>
            </button>
            {props.relabelOpen && (
              <RelabelMenu
                current={null}
                onChoose={props.onRelabel}
                onClose={() => props.onRelabelOpen(false)}
              />
            )}
          </span>
          {assign && (
            <span className="lcr-i2-menu-anchor">
              <button
                type="button"
                className="lcr-bulk-action"
                aria-haspopup="menu"
                aria-expanded={assign.open}
                aria-keyshortcuts="M"
                disabled={empty}
                onClick={() => assign.onOpenChange(!assign.open)}
              >
                Assign
                <kbd className="lcr-kbd">M</kbd>
              </button>
              {assign.open && (
                <AssignMenu
                  assignment={assign.assignment}
                  onChoose={assign.onChoose}
                  onClose={() => assign.onOpenChange(false)}
                />
              )}
            </span>
          )}
          <span className="lcr-snooze-anchor">
            {props.snoozed ? (
              <button
                type="button"
                className="lcr-bulk-action"
                disabled={empty}
                onClick={props.onUnsnooze}
              >
                Unsnooze
              </button>
            ) : (
              <button
                type="button"
                className="lcr-bulk-action"
                aria-haspopup="dialog"
                aria-expanded={props.snoozeMenu ? true : undefined}
                aria-keyshortcuts="H"
                aria-label="Snooze"
                title="Snooze (H)"
                disabled={empty}
                onClick={props.onSnooze}
              >
                {/* A narrow column shows the clock alone (inbox-v2.css, the bar's last step). */}
                <ClockIcon size={16} className="lcr-bulk-snooze-icon" />
                <span className="lcr-bulk-snooze-word">Snooze</span>
                <kbd className="lcr-kbd">H</kbd>
              </button>
            )}
            {props.snoozeMenu}
          </span>
        </>
      }
    />
  );
}

export type RowView = {
  showCampaign: boolean;
  focusedId: string | null;
  openId: string | null;
  /** The one row Tab lands on (`ListColumn`): the list is a single Tab stop. */
  tabbableId: string | null;
  selected: ReadonlySet<string>;
  selecting: boolean;
  /** Rows kept from an older list: to open and read, with no box to tick and no hold to select. */
  readOnly: boolean;
  onOpen: (row: InboxConversation) => void;
  onToggle: (id: string) => void;
  onLongPress: (id: string) => void;
  /** A row's own Snooze, where a pointer can hover; null on touch. */
  onSnooze: ((row: InboxConversation) => void) | null;
  /** Snooze's choices, where they are open on a row. */
  snoozeMenu: (row: InboxConversation) => ReactNode;
  /** The reply agent's state on a row, for a person with the switch on; null draws nothing. */
  agentState: (id: string) => InboxAgentState | null;
  /** The viewer, so a row assigned to them says "You". */
  you: string | null;
};

function rows(list: readonly InboxConversation[], view: RowView, prefix: string) {
  return list.map((row) => (
    <ConversationRow
      key={`${prefix}${row.id}`}
      row={row}
      showCampaign={view.showCampaign}
      focused={row.id === view.focusedId}
      open={row.id === view.openId}
      selected={view.selected.has(row.id)}
      selecting={view.selecting}
      readOnly={view.readOnly}
      agent={view.agentState(row.id)}
      you={view.you}
      pinned={prefix !== ""}
      tabIndex={prefix === "" && row.id === view.tabbableId ? 0 : -1}
      onOpen={() => view.onOpen(row)}
      onToggle={() => view.onToggle(row.id)}
      onLongPress={() => view.onLongPress(row.id)}
      onSnooze={view.onSnooze ? () => view.onSnooze?.(row) : undefined}
      // The menu opens on the list's row, not on its pinned copy.
      snoozeMenu={prefix === "" ? view.snoozeMenu(row) : null}
    />
  ));
}

/**
 * The reply agent filter's refusal to count past its limit (step 9): the server's own words, where
 * the rows would be, and why. It is never a toast, and never an approximate count.
 */
function ListRefused(props: {
  refused: { limit: number; message: string };
  onClearAgent: () => void;
}) {
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">{props.refused.message}</p>
      {/* The server counts the list with every filter but the agent state: this list, not a match. */}
      <p className="lcr-list-empty-detail">
        More than {appCount(props.refused.limit)} conversations in this list.
      </p>
      <div className="lcr-list-empty-links">
        <button type="button" className="lcr-list-empty-link" onClick={props.onClearAgent}>
          Show any agent state
        </button>
      </div>
    </div>
  );
}

/** What the strip's head names: the work row who answers next reads from (LEA-494). */
const STRIP_NAMES = {
  meeting: { name: "Meeting requests", rest: "that asked to meet" },
  needs_reply: { name: "Needs reply", rest: "that need a reply" },
} as const;

/**
 * The Highlights strip: up to three replies from the place who answers next reads from, Meeting
 * requests while any wait, else Needs reply. They stay in the list below too.
 */
function HighlightsHead(props: {
  place: NextPlace;
  total: number;
  shown: number;
  onSeeAll: () => void;
}) {
  const words = STRIP_NAMES[props.place];
  return (
    <div className="lcr-track-head" data-track="important">
      <h3 className="lcr-track-name">
        {words.name}
        <span className="lcr-track-count">
          <span className="lcr-visually-hidden">, </span>
          {appCount(props.total)}
          <span className="lcr-visually-hidden">
            {" "}
            {props.total === 1 ? "conversation" : "conversations"}
          </span>
        </span>
        <span className="lcr-track-hint"> · newest {props.shown}</span>
      </h3>
      {props.total > props.shown && (
        <button type="button" className="lcr-track-more" onClick={props.onSeeAll}>
          Show all
          <span className="lcr-visually-hidden"> {words.rest}</span>
        </button>
      )}
    </div>
  );
}

/** The skeleton's rows: about a screen of them, never a page's worth. */
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5] as const;

/**
 * A first load's rows, before any list is shown (design review "Loading", P1): six rows in the real
 * row's shape, subject and time, lead, preview, two chips. They wait the thread's 180 ms beat before
 * showing, so a quick answer never flashes them, and breathe only where motion is welcome; the
 * heading above them is real from the first frame. A screen reader hears one line.
 */
function ListSkeleton() {
  return (
    <div className="lcr-list-skeleton">
      <p className="lcr-visually-hidden" role="status">
        Loading conversations
      </p>
      <div className="lcr-opening" aria-hidden="true">
        {SKELETON_ROWS.map((row) => (
          <div key={row} className="lcr-skeleton-row">
            <span className="lcr-skeleton-row-line">
              <span className="lcr-skeleton" data-width="row-subject" />
              <span className="lcr-skeleton" data-width="row-time" />
            </span>
            <span className="lcr-skeleton-row-line">
              <span className="lcr-skeleton" data-width="row-lead" />
            </span>
            <span className="lcr-skeleton-row-line">
              <span className="lcr-skeleton" data-width="row-preview" />
            </span>
            <span className="lcr-skeleton-row-chips">
              <span className="lcr-skeleton" data-width="row-chip" />
              <span className="lcr-skeleton" data-width="row-chip-wide" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ListBody(props: {
  main: InboxPage | undefined;
  /** The rows are the list shown before, kept while the next loads: dimmed, and busy. */
  stale: boolean;
  mainFailed: boolean;
  /** The server's reference for the failure, quoted in the notice; null when it gave none. */
  failureReference: string | null;
  retrying: boolean;
  /** The reply agent filter's refusal, in the rows' place; null otherwise. */
  refused: { limit: number; message: string } | null;
  strip: { place: NextPlace; rows: readonly InboxConversation[]; total: number } | null;
  /** What an empty list says, and the ways on it offers. */
  empty: EmptyContext;
  /** Said above the rows while the search index covers only part of the list. */
  coverage: string | null;
  sort: ListSort;
  offset: number;
  rows: RowView;
  onRetry: () => void;
  onPage: (offset: number) => void;
  /** Takes the agent state off and keeps the rest: the refusal's way on. */
  onClearAgent: () => void;
  /** Opens the place the strip reads from. */
  onSeeAll: (place: NextPlace) => void;
}) {
  const { main, strip } = props;
  const list = main?.conversations ?? [];
  return (
    <div
      className="lcr-list-body"
      // Rows kept under a failure are not on their way: dimmed, but not busy.
      aria-busy={(props.stale && !props.mainFailed) || undefined}
      data-stale={props.stale || undefined}
    >
      {props.coverage !== null && list.length > 0 && (
        <p className="lcr-list-coverage" role="note">
          {props.coverage}
        </p>
      )}
      {props.mainFailed && (
        <ListFailed
          reference={props.failureReference}
          retrying={props.retrying}
          onRetry={props.onRetry}
        />
      )}
      {strip && (
        <>
          <HighlightsHead
            place={strip.place}
            total={strip.total}
            shown={strip.rows.length}
            onSeeAll={() => props.onSeeAll(strip.place)}
          />
          {rows(strip.rows, props.rows, "pinned:")}
          <div className="lcr-track-head" data-track="fresh">
            <h3 className="lcr-track-name">
              All replies
              <span className="lcr-track-hint">
                {" "}
                · {props.sort === "newest" ? "newest first" : "oldest first"}
              </span>
            </h3>
          </div>
        </>
      )}
      {rows(list, props.rows, "")}
      {main && list.length === 0 && <ListEmpty context={props.empty} />}
      {props.refused && <ListRefused refused={props.refused} onClearAgent={props.onClearAgent} />}
      {!main && !props.mainFailed && !props.refused && <ListSkeleton />}
      {main && (
        <Pager
          offset={props.offset}
          shown={list.length}
          total={main.total}
          pageSize={INBOX_PAGE_SIZE}
          onPage={props.onPage}
          noun="Conversations"
        />
      )}
    </div>
  );
}

/** The page's reports, newest at the foot: held replies (`held-sends.tsx`), then the last action. */
export function ToastView(props: {
  toast: Toast | null;
  onUndo: () => void;
  /** The toast's other way on, such as Try again (W5). */
  onAction: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="lcr-i2-toast-region" role="status" aria-live="polite">
      {props.children}
      {props.toast && (
        <div className="lcr-bulk-toast lcr-i2-toast">
          <span>{props.toast.text}</span>
          {props.toast.undo && (
            <button type="button" className="lcr-bulk-toast-action" onClick={props.onUndo}>
              Undo
            </button>
          )}
          {props.toast.action && (
            <button type="button" className="lcr-bulk-toast-action" onClick={props.onAction}>
              {props.toast.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
